import { useCallback } from 'react';
import { Field } from '../ui/Field';
import { inputCls } from '../ui/styles';
import { useLatestLoad } from '../../hooks/useLatestLoad';
import { fetchStock } from '../adminApi';
import type { GiftTier } from '../../core/types/site';
import type { RequestOptions } from '../../core/types/backend';
type Props = {
  /** De treden naast gratis verzending. */
  tiers: GiftTier[];
  onChange: (ruleId: string, patch: Pick<GiftTier, 'stockItemId' | 'choiceStockItems'>) => void;
};

/**
 * Per cadeau: welk voorraadartikel er bij een betaalde bestelling afgaat (afschrijven, oorzaak
 * cadeau). Zo klopt de voorraad zonder dat Sarah het staffelcadeau met de hand afboekt. Een trede
 * met keuzes krijgt per keuze een artikel. Leeg = niets afboeken (bijv. een consult).
 */
export function GiftStockLinks({
  tiers,
  onChange
}: Props) {
  const load = useCallback((opts: RequestOptions) => fetchStock(opts), []);
  const {
    data,
    status
  } = useLatestLoad(load);
  if (tiers.length === 0) return null;
  const items = data ? [...data.items].sort((a, b) => a.name.localeCompare(b.name, 'nl')) : [];
  const select = (value: string | undefined, onPick: (id: string | undefined) => void, label: string) => <Field label={label} data-id="element-4023" data-mp="2l385xczecoxa">
      <select value={value ?? ''} onChange={e => onPick(e.target.value || undefined)} disabled={!data} className={`${inputCls} sm:max-w-sm`} data-id="element-4024" data-mp="2l2ygprm6elsn">
        <option value="" data-id="element-4025" data-mp="2u4fs4won1zb0">Niets afboeken</option>
        {items.map(i => <option key={i.id} value={i.id} data-id="element-4026" data-mp="2u463bcfbxy6w">
            {i.name}
            {i.brand ? ` · ${i.brand}` : ''}
          </option>)}
        {/* Een gekoppeld artikel dat niet meer bestaat, blijft zichtbaar tot ze iets anders kiest. */}
        {value && data && !items.some(i => i.id === value) && <option value={value} data-id="element-4027" data-mp="38skgag6gn316">Onbekend artikel ({value})</option>}
      </select>
    </Field>;
  return <section aria-labelledby="gift-stock-title" className="mt-6 border-t border-line pt-5" data-id="element-4028" data-mp="3dqc694njof2q">
      <h3 id="gift-stock-title" className="text-[14px] text-ink" data-id="element-4029" data-mp="3hrxol5iullkb">
        Van de voorraad bij een betaalde bestelling
      </h3>
      <p className="mt-1 text-[13px] text-muted" data-id="element-4030" data-mp="1dqfkud0vhl5t">Eén stuk gaat van de webshopvoorraad af, met de bestelling als verwijzing. In Voorraad staat het als afgeschreven: cadeau.</p>
      {status === 'error' && !data && <p className="mt-2 text-[13px] text-clay" data-id="element-4031" data-mp="1mr3wj12sn9fj">De voorraad kon niet geladen worden. Ververs de pagina om een artikel te kiezen.</p>}
      <div className="mt-4 space-y-5" data-id="element-4032" data-mp="21egn547gk5cs">
        {tiers.map(t => t.choices?.length ? <fieldset key={t.ruleId} className="space-y-3" data-id="element-4033" data-mp="1wfvnp7015ppe">
              <legend className="text-[13px] text-ink" data-id="element-4034" data-mp="2ag6e68xfh2a1">{t.title}</legend>
              {t.choices.map(c => <div key={c} data-id="element-4035" data-mp="26el5z0vu9c66">
                  {select(t.choiceStockItems?.[c], id => {
            const {
              [c]: _old,
              ...rest
            } = t.choiceStockItems ?? {};
            onChange(t.ruleId, {
              choiceStockItems: id ? {
                ...rest,
                [c]: id
              } : rest
            });
          }, c)}
                </div>)}
            </fieldset> : <div key={t.ruleId} data-id="element-4036" data-mp="60kzqpnpxyxr">{select(t.stockItemId, id => onChange(t.ruleId, {
          stockItemId: id
        }), t.title)}</div>)}
      </div>
    </section>;
}